import React from "react";
import { Accordion } from "react-bootstrap";
import Resources from "../../../../types/Resources";
import { Transformation } from "../../../../types/functions";
import TextArea from "../../../Common/TextArea";
import LabelCheckbox from "../../../Common/LabelCheckbox";
import HotWorkConditionsFormState from "../../../../types/forms/Permits/EnergyHarbourPermit/HotWorkFormState";
import SignatureDisplay from "../../../Signatures/SignatureDisplay";
import BaseUserInfo from "../../../../types/BaseUserInfo";
import { SignatureManager } from "../../../../hooks/useSignature";
import SignatureModal from "../../../Signatures/SignatureModal";
import CounterSignatureRequestInput from "../CounterSignatureRequestInput";
import { CounterSignatureRequestInputManager } from "../../../../hooks/useCounterSignatureRequestInput";
import { CounterSignatureRequest_isActive } from "../../../../types/CounterSignatureRequest";
import { HotWorkSection } from "../../../../common/enums";

type HotWorkConditionsAccordionItemProps = {
    hotWorkSection: HotWorkSection;
    resources: Resources;
    formState: HotWorkConditionsFormState;
    onChange: (transformation: Transformation<HotWorkConditionsFormState>, hotWorkSection: HotWorkSection) => void;
    baseUserInfo: BaseUserInfo;
    canManageContent: boolean;
};

function elementId(hotWorkSection: HotWorkSection, key: string) {
    return `${hotWorkSection}__${key}`;
}

function HotWorkConditionsAccordionItem({
    resources,
    onChange,
    formState,
    canManageContent,
    hotWorkSection
}: HotWorkConditionsAccordionItemProps) {
    function changeHandler(key: keyof HotWorkConditionsFormState, value: any) {
        onChange(
            (prevValue) => ({
                ...prevValue,
                [key]: { ...prevValue[key], value: value }
            }),
            hotWorkSection
        );
    }

    const eventKey = `hot-work-conditions-${hotWorkSection}`;
    const headerResourceKey = `${hotWorkSection}Conditions`;

    return (
        <>
            <Accordion.Item eventKey={eventKey}>
                <Accordion.Header>
                    <span className="fw-bold">{resources.get(headerResourceKey)}</span>
                </Accordion.Header>
                <Accordion.Body>
                    <ul className="list-unstyled">
                        <li>
                            <LabelCheckbox
                                checked={formState.isPhoneClassified.value}
                                id={elementId(hotWorkSection, "is-phone-classified")}
                                label={resources.get("IsPhoneClassified")}
                                onChange={(checked) => changeHandler("isPhoneClassified", checked)}
                                readOnly={!canManageContent}
                            />
                        </li>
                        <li className="mt-3">
                            <LabelCheckbox
                                checked={formState.isCleanedUpForFireProtection.value}
                                id={elementId(hotWorkSection, "is-cleaned-up-for-fire-protection")}
                                label={resources.get("IsCleanedUpForFireProtection")}
                                onChange={(checked) => changeHandler("isCleanedUpForFireProtection", checked)}
                                readOnly={!canManageContent}
                            />
                        </li>
                        <li className="mt-3">
                            <LabelCheckbox
                                checked={formState.isGasDeclared.value}
                                id={elementId(hotWorkSection, "is-gas-declared")}
                                label={resources.get("IsGasDeclared")}
                                onChange={(checked) => changeHandler("isGasDeclared", checked)}
                                readOnly={!canManageContent}
                            />
                        </li>
                        <li className="mt-3">
                            <LabelCheckbox
                                checked={formState.isContinousGasMeasurements.value}
                                id={elementId(hotWorkSection, "is-continuous-gas-measurements")}
                                label={resources.get("IsContinousGasMeasurements")}
                                onChange={(checked) => changeHandler("isContinousGasMeasurements", checked)}
                                readOnly={!canManageContent}
                            />
                        </li>
                        <li className="mt-3">
                            <LabelCheckbox
                                checked={formState.isSparkProtected.value}
                                id={elementId(hotWorkSection, "is-spark-protected")}
                                label={resources.get("IsSparkProtected")}
                                onChange={(checked) => changeHandler("isSparkProtected", checked)}
                                readOnly={!canManageContent}
                            />
                        </li>
                        <li className="mt-3">
                            <LabelCheckbox
                                checked={formState.isWellsProtected.value}
                                id={elementId(hotWorkSection, "is-wells-protected")}
                                label={resources.get("IsWellsProtected")}
                                onChange={(checked) => changeHandler("isWellsProtected", checked)}
                                readOnly={!canManageContent}
                            />
                        </li>
                        <li className="mt-3 form-group d-flex align-items-center">
                            <input
                                className={`form-check-input me-1 ${!canManageContent ? "read-only-check" : ""}`}
                                id={elementId(hotWorkSection, "fire-extinguisher-is-checked")}
                                type="checkbox"
                                checked={formState.fireExtinguisherIsChecked.value}
                                onChange={(e) =>
                                    canManageContent
                                        ? changeHandler("fireExtinguisherIsChecked", e.target.checked)
                                        : false
                                }
                            />
                            <label
                                htmlFor={elementId(hotWorkSection, "fire-extinguisher-is-checked")}
                                className={!canManageContent ? "pe-none" : ""}
                            >
                                {resources.get("PowderExtinguisher")}
                            </label>
                            <input
                                type="number"
                                min="0"
                                style={{ width: "80px" }}
                                className={`form-control d-inline ms-1 me-1 ${
                                    !canManageContent ? "read-only-input" : ""
                                }`}
                                id={elementId(hotWorkSection, "fire-extinguisher-count")}
                                value={formState.fireExtinguisherCount.value ?? ""}
                                onChange={(e) => changeHandler("fireExtinguisherCount", e.target.value)}
                                aria-errormessage={elementId(hotWorkSection, "fire-extinguisher-count__error-message")}
                                readOnly={!canManageContent}
                            />
                            <label htmlFor={elementId(hotWorkSection, "fire-extinguisher-count")} className={!canManageContent ? "pe-none" : ""}>
                                {resources.get("WhereEachWeighs")}
                            </label>
                            <input
                                type="number"
                                min="0"
                                style={{ width: "80px" }}
                                className={`form-control d-inline ms-1 me-1 ${
                                    !canManageContent ? "read-only-input" : ""
                                }`}
                                id={elementId(hotWorkSection, "fire-extinguisher-kg")}
                                value={formState.fireExtinguisherKg.value ?? ""}
                                onChange={(e) => changeHandler("fireExtinguisherKg", e.target.value)}
                                aria-errormessage={elementId(hotWorkSection, "fire-extinguisher-kg__error-message")}
                                readOnly={!canManageContent}
                            />
                            <label htmlFor={elementId(hotWorkSection, "fire-extinguisher-kg")} className={!canManageContent ? "pe-none" : ""}>
                                {resources.get("KgArePlacedAtWorkPlace")}
                            </label>
                            <div>
                                <span id={elementId(hotWorkSection, "fire-extinguisher-count__error-message")} className="text-danger">
                                    {formState.fireExtinguisherCount.errorMessage}
                                </span>
                                <span id={elementId(hotWorkSection, "fire-extinguisher-kg__error-message")} className="text-danger">
                                    {formState.fireExtinguisherKg.errorMessage}
                                </span>
                            </div>
                        </li>
                        <li className="mt-3">
                            <LabelCheckbox
                                checked={formState.isWaterFromFirePostInPlace.value}
                                id={elementId(hotWorkSection, "is-water-from-fire-post-in-place")}
                                label={resources.get("IsWaterFromFirePostInPlace")}
                                onChange={(checked) => changeHandler("isWaterFromFirePostInPlace", checked)}
                                readOnly={!canManageContent}
                            />
                        </li>
                        <li className="mt-3 d-flex align-items-center form-group">
                            <input
                                id={elementId(hotWorkSection, "work-place-monitored-is-checked")}
                                type="checkbox"
                                className={`me-1 form-check-input ${!canManageContent ? "read-only-check" : ""}`}
                                checked={formState.workPlaceMonitoredIsChecked.value}
                                onChange={(e) =>
                                    canManageContent
                                        ? changeHandler("workPlaceMonitoredIsChecked", e.target.checked)
                                        : false
                                }
                            />
                            <label
                                htmlFor={elementId(hotWorkSection, "work-place-monitored-is-checked")}
                                className={!canManageContent ? "pe-none" : ""}
                            >
                                {resources.get("WorkPlaceMonitoredBy")}
                            </label>
                            <input
                                id={elementId(hotWorkSection, "fire-guard-count")}
                                className={`form-control ms-1 me-1 ${!canManageContent ? "read-only-input" : ""}`}
                                type="number"
                                style={{ width: "80px" }}
                                min="0"
                                value={formState.fireGuardCount.value ?? ""}
                                onChange={(e) => changeHandler("fireGuardCount", e.target.value)}
                                aria-errormessage={elementId(hotWorkSection, "fire-guard-count__error-message")}
                                readOnly={!canManageContent}
                            />
                            <label htmlFor={elementId(hotWorkSection, "fire-guard-count")} className={!canManageContent ? "pe-none" : ""}>
                                {resources.get("FireGuardsAlsoMonitorPostWork")}:
                            </label>
                            <input
                                id={elementId(hotWorkSection, "post-work-place-monitored-hours")}
                                className={`form-control ms-1 me-1 ${!canManageContent ? "read-only-input" : ""}`}
                                type="number"
                                style={{ width: "80px" }}
                                min="0"
                                value={formState.postWorkPlaceMonitoredHours.value ?? ""}
                                onChange={(e) => changeHandler("postWorkPlaceMonitoredHours", e.target.value)}
                                aria-errormessage={elementId(hotWorkSection, "post-work-place-monitored-hours__error-message")}
                                readOnly={!canManageContent}
                            />
                            <label
                                htmlFor={elementId(hotWorkSection, "post-work-place-monitored-hours")}
                                className={!canManageContent ? "pe-none" : ""}
                            >
                                {resources.get("Hours")}
                            </label>
                            <div>
                                <span id={elementId(hotWorkSection, "fire-guard-count__error-message")} className="text-danger">
                                    {formState.fireGuardCount.errorMessage}
                                </span>
                                <span id={elementId(hotWorkSection, "post-work-place-monitored-hours__error-message")} className="text-danger">
                                    {formState.postWorkPlaceMonitoredHours.errorMessage}
                                </span>
                            </div>
                        </li>
                        <li className="mt-3 d-flex align-items-center form-group">
                            <input
                                id={elementId(hotWorkSection, "fire-detectors-turned-off-is-checked")}
                                className={`me-1 form-check-input ${!canManageContent ? "read-only-check" : ""}`}
                                type="checkbox"
                                checked={formState.fireDetectorsTurnedOffIsChecked.value}
                                onChange={(e) =>
                                    canManageContent
                                        ? changeHandler("fireDetectorsTurnedOffIsChecked", e.target.checked)
                                        : false
                                }
                            />
                            <label
                                htmlFor={elementId(hotWorkSection, "fire-detectors-turned-off-is-checked")}
                                className={!canManageContent ? "pe-none" : ""}
                            >
                                {resources.get("FireDetectorsTurnedOffSectionsNames")}:
                            </label>
                            <input
                                id={elementId(hotWorkSection, "fire-detectors-turned-off-section-names")}
                                className={`form-control d-inline ms-1 me-1 ${
                                    !canManageContent ? "read-only-input" : ""
                                }`}
                                type="text"
                                style={{ width: "360px" }}
                                value={formState.fireDetectorsTurnedOffSectionNames.value ?? ""}
                                onChange={(e) => changeHandler("fireDetectorsTurnedOffSectionNames", e.target.value)}
                                aria-description={resources.get("FireDetectorsTurnedOffSectionNames")}
                                readOnly={!canManageContent}
                            />
                        </li>
                        <li className="mt-3 d-flex align-items-center form-group">
                            <input
                                id={elementId(hotWorkSection, "no-hotwork-with-flammable-materials-used-sub-30-celcius")}
                                type="checkbox"
                                className={`form-check-input me-1 ${!canManageContent ? "read-only-check" : ""}`}
                                checked={formState.noHotworkWithFlammableMaterialsUsedSub30Celcius.value}
                                onChange={(e) =>
                                    canManageContent
                                        ? changeHandler(
                                              "noHotworkWithFlammableMaterialsUsedSub30Celcius",
                                              e.target.checked
                                          )
                                        : false
                                }
                            />
                            <label
                                htmlFor={elementId(hotWorkSection, "no-hotwork-with-flammable-materials-used-sub-30-celcius")}
                                className={!canManageContent ? "pe-none" : ""}
                            >
                                {resources.get("NoHotworkWithFlammableMaterialsUsedSub30Celcius")}
                            </label>
                            <select
                                id={elementId(hotWorkSection, "distance-from-pumps-and-ventilation-meters")}
                                className="form-select ms-1 me-1"
                                style={{ width: "80px" }}
                                value={formState.distanceFromPumpsAndVentilationMeters.value ?? ""}
                                onChange={(e) => changeHandler("distanceFromPumpsAndVentilationMeters", e.target.value)}
                                disabled={!canManageContent}
                            >
                                <option value="15">15</option>
                                <option value="25">25</option>
                            </select>
                            <label htmlFor={elementId(hotWorkSection, "distance-from-pumps-and-ventilation-meters")}>
                                {resources.get("DistanceFromPumpsAndVentilationMeters")}
                            </label>
                        </li>
                        <li className="mt-3">
                            <LabelCheckbox
                                checked={formState.noHotWorkAtQuay.value}
                                id={elementId(hotWorkSection, "no-hot-work-at-quay")}
                                label={resources.get("NoHotWorkAtQuay")}
                                onChange={(c) => changeHandler("noHotWorkAtQuay", c)}
                                readOnly={!canManageContent}
                            />
                        </li>
                        <li className="mt-3">
                            <LabelCheckbox
                                checked={formState.isShipInformedAboutFlammablesSub30Celcius.value}
                                id={elementId(hotWorkSection, "is-ship-informed-about-flammables-sub-30-celcius")}
                                label={resources.get("IsShipInformedAboutFlammablesSub30Celcius")}
                                onChange={(c) => changeHandler("isShipInformedAboutFlammablesSub30Celcius", c)}
                                readOnly={!canManageContent}
                            />
                        </li>
                        <li className="mt-3 form-group d-flex align-items-center">
                            <input
                                id={elementId(hotWorkSection, "inform-close-depot-before-work-is-checked")}
                                type="checkbox"
                                className={`form-check-input me-1 ${!canManageContent ? "read-only-check" : ""}`}
                                checked={formState.informCloseDepotBeforeWorkIsChecked.value}
                                onChange={(e) =>
                                    canManageContent
                                        ? changeHandler("informCloseDepotBeforeWorkIsChecked", e.target.checked)
                                        : false
                                }
                            />
                            <label
                                htmlFor={elementId(hotWorkSection, "inform-close-depot-before-work-is-checked")}
                                className={!canManageContent ? "pe-none" : ""}
                            >
                                {resources.get("IsCloseDepotInformedBeforeWorkIsStarted")}
                            </label>
                            <input
                                id={elementId(hotWorkSection, "name-of-close-depot-before-work-is-started-info")}
                                className={`form-control d-inline ms-1 me-1 ${
                                    !canManageContent ? "read-only-input" : ""
                                }`}
                                type="text"
                                style={{ width: "360px" }}
                                value={formState.nameOfCloseDepotBeforeWorkIsStartedInfo.value ?? ""}
                                onChange={(e) =>
                                    changeHandler("nameOfCloseDepotBeforeWorkIsStartedInfo", e.target.value)
                                }
                                readOnly={!canManageContent}
                            />
                        </li>
                        <li className="mt-3">
                            <TextArea
                                textAreaId={elementId(hotWorkSection, "other-information")}
                                errorMessage={formState.otherInformation.errorMessage}
                                value={formState.otherInformation.value ?? ""}
                                labelText={resources.get("Other")}
                                onChange={(s) => changeHandler("otherInformation", s)}
                                readOnly={!canManageContent}
                            />
                        </li>
                    </ul>
                </Accordion.Body>
            </Accordion.Item>
        </>
    );
}

export default HotWorkConditionsAccordionItem;